import { useEffect, useState } from 'react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { financeApi } from '@/api/finance.api';
import type { FinanceCompliance } from '@/types/finance';
import { showApiError } from '@/utils/apiError';

export function FinanceCompliancePage() {
  const [compliance, setCompliance] = useState<FinanceCompliance | null>(null);
  const [activating, setActivating] = useState<string | null>(null);

  const load = () => financeApi.platformCompliance().then(setCompliance).catch((e) => showApiError(e, 'Failed to load compliance'));

  useEffect(() => {
    load();
  }, []);

  const activate = (countryCode: string, packCode: string) => {
    setActivating(packCode);
    financeApi
      .activateCompliancePack(countryCode, packCode)
      .then(() => load())
      .catch((e) => showApiError(e, 'Activation failed'))
      .finally(() => setActivating(null));
  };

  return (
    <div>
      <PageHeader
        title="Global compliance"
        description="Country tax packs for GST, VAT, sales tax, and withholding"
        breadcrumbs={[
          { label: 'Org', to: '/org' },
          { label: 'Finance', to: '/finance' },
          { label: 'Tax', to: '/finance/tax' },
          { label: 'Compliance' },
        ]}
      />

      {compliance?.activeProfiles.length ? (
        <div className="mb-6 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-900 dark:border-green-800 dark:bg-green-950/40 dark:text-green-200">
          Active: {compliance.activeProfiles.map((p) => `${p.countryCode} — ${p.packName}`).join(', ')}
        </div>
      ) : (
        <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100">
          No compliance pack activated yet. Choose a country pack below to configure tax validation.
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        {compliance?.availablePacks.map((pack) => (
          <div key={pack.packCode} className="card p-4">
            <h3 className="font-semibold">{pack.packName}</h3>
            <p className="text-sm text-slate-600 mt-1">Regime: {pack.taxRegime}</p>
            <p className="text-xs text-slate-500 mt-2">{pack.supportedTaxTypes.join(', ')}</p>
            <Button
              className="mt-3"
              size="sm"
              variant="secondary"
              disabled={activating === pack.packCode}
              onClick={() => activate(pack.countryCode, pack.packCode)}
            >
              {activating === pack.packCode ? 'Activating…' : 'Activate'}
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}
